/* eslint-disable max-len */
import { getAuthHeaders } from '../actions/authActions';
import tempMeeApi from '../apis/tempMee';

export const getCredentialsV2 = async (userId, userType) => {
  const headers = await getAuthHeaders();

  return tempMeeApi.get(`credential-service/users/${userId}/credentials`, {
    params: {
      userType,
    },
    headers: {
      ...headers,
    },
  });
};

export const patchCredentials = async (userId, education, insurances) => {
  const headers = await getAuthHeaders();

  return tempMeeApi.patch(
    `admin/users/${userId}/specialtiesAndProcedures`,
    { education, insurances },
    { headers },
  );
};

export const patchCredentialsV2 = async (userId, userType, education, insurances, softwares) => {
  const headers = await getAuthHeaders();

  return tempMeeApi.patch(
    `credential-service/users/${userId}/credentials?userType=${userType}`,
    {
      education,
      insurances,
      ...(softwares && { softwares }),
    },
    { headers },
  );
};

/** get prof licenses from monolith with secure s3 image url */ 
export const getProtectedProfLicenses = async (userId) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.get(`admin/users/${userId}/licenses`, { headers });
};


export const getProfessionalAttestation = async (userId, code) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.get(`credential-service/users/${userId}/consents/${code}`, {
    headers,
  });
};

export const getDentistAttestation = async (officeId, code) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.get(`credential-service/admin/consents/${code}`, {
    headers,
    params: {
      office_id: officeId
    }
  });
};

export const updateDentistAttestation = async ({ officeId, code, isEnabled }) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.patch(
    `credential-service/admin/consents/${code}`,
    { is_enabled: isEnabled },
    {
      headers,
      params: {
        office_id: officeId
      }
    },
  );
};

export const getAuthorizations = async (professionalId) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.get(`credential-service/professionals/${professionalId}/authorizations`, {
    headers,
  });
};

export const deleteAuthorization = async (professionalId, authorizationId) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.delete(`credential-service/professionals/${professionalId}/authorizations/${authorizationId}`, {
    headers,
  });
};

export const createAuthorization = async (professionalId, { state, profession }) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.put(
    `credential-service/professionals/${professionalId}/authorizations`,
    { state, profession },
    { headers },
  );
};

/* eslint-disable camelcase, no-underscore-dangle */
// --- V2 credential update APIs ---

export const updateCredentialArtifact = async (
  professionalId,
  credentialId,
  { credential_type, issue_date, expire_date, status, files },
) => {
  const headers = await getAuthHeaders();
  const formData = new FormData();

  if (credential_type) {
    formData.append('credential_type', credential_type);
  }
  if (issue_date) {
    formData.append('issue_date', issue_date);
  }
  if (expire_date !== undefined) {
    formData.append('expire_date', expire_date ?? '');
  }
  if (status) {
    formData.append('status', status);
  }

  if (files?.length) {
    files.forEach((file) => {
      if (file._local && file._file) {
        formData.append('files', file._file);
      }
    });
  }

  return tempMeeApi.put(
    `credential-service/professionals/${professionalId}/credentials/artifact/${credentialId}`,
    formData,
    {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export const updateCredentialNumber = async (
  professionalId,
  credentialId,
  data,
) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.put(
    `credential-service/professionals/${professionalId}/credentials/number/${credentialId}`,
    data,
    { headers },
  );
};

export const updateCredentialReference = async (
  professionalId,
  credentialId,
  data,
) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.put(
    `credential-service/professionals/${professionalId}/credentials/reference/${credentialId}`,
    data,
    { headers },
  );
};

export const updateCredentialNpi = async (
  professionalId,
  credentialId,
  data,
) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.put(
    `credential-service/professionals/${professionalId}/credentials/npi/${credentialId}`,
    data,
    { headers },
  );
};
// --- V2 credential create APIs ---

export const createCredentialArtifact = async (
  professionalId,
  authorizationId,
  { credential_type, issue_date, expire_date, status, files },
) => {
  const headers = await getAuthHeaders();
  const formData = new FormData();

  if (credential_type) {
    formData.append('credential_type', credential_type);
  }
  if (issue_date) {
    formData.append('issue_date', issue_date);
  }
  if (expire_date !== undefined) {
    formData.append('expire_date', expire_date ?? '');
  }
  if (status) {
    formData.append('status', status);
  }

  if (files?.length) {
    files.forEach((file) => {
      if (file._local && file._file) {
        formData.append(`files`, file._file);
      }
    });
  }

  return tempMeeApi.post(
    `credential-service/professionals/${professionalId}/authorizations/${authorizationId}/credentials/artifact`,
    formData,
    {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export const createCredentialNumber = async (
  professionalId,
  authorizationId,
  data,
) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.post(
    `credential-service/professionals/${professionalId}/authorizations/${authorizationId}/credentials/number`,
    data,
    { headers },
  );
};

export const createCredentialReference = async (
  professionalId,
  authorizationId,
  data,
) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.post(
    `credential-service/professionals/${professionalId}/authorizations/${authorizationId}/credentials/reference`,
    data,
    { headers },
  );
};

export const createCredentialNpi = async (
  professionalId,
  authorizationId,
  data,
) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.post(
    `credential-service/professionals/${professionalId}/authorizations/${authorizationId}/credentials/npi`,
    data,
    { headers },
  );
};
export const getCredentialAuditTrail = async (professionalId) => {
  const headers = await getAuthHeaders();
  return tempMeeApi.get(
    `credential-services/admin/professionals/${professionalId}/credential-audit-trail`,
    { headers },
  );
};
/* eslint-enable camelcase, no-underscore-dangle */
